import { useCallback, useState, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Button, Chip, TextField } from 'bf-ui';
import { NumericFormat } from 'react-number-format';

import Image from 'next/image';

import styles from './depositWithPartner.section.scss';

import websiteConfig from '../../../configs/websiteConfig';
import globalEmitter, { emits } from '../../../emitter/global.emitter';
import { deposit_req } from '../../../api/payment.api';

import PresentIcon from '../../../assets/images/icons/present.svg';
import { remainingMinSecs } from '../../../helpers/calculations.helper';

export default function DepositWithPartnerSection({ activeOperator }) {
  const intl = useIntl();

  const iframeRef = useRef();

  const amountsWithPresent = useRef([]);

  const processingTimerRef = useRef(0);
  const [depositMessage, setDepositMessage] = useState('');

  const [remainingData, setRemainingData] = useState({
    processingTime: null,
    remainingText: '',
  });

  const [amounts] = useState(
    activeOperator.DepositOptions || [50, 100, 500, 1000]
  );

  const [amount, setAmount] = useState(activeOperator?.SelectedDeposit || 50);
  const [depositUrl, setDepositUrl] = useState('');
  const [depositLoading, setDepositLoading] = useState(false);

  const localAlertTimeoutRef = useRef(0);
  const [localAlertData, setLocalAlertData] = useState(null);

  const onIframeEvent = useCallback((ev) => {
    if (ev?.data) {
      if (ev.data === 'IPA_ONCOMPLETE') {
        globalEmitter.emit(emits.ALERT, {
          type: 'success',
          message: `Your deposit was successful!`,
        });
        setDepositUrl('');
      }

      if (ev.data === 'IPA_ONFAIL') {
        globalEmitter.emit(emits.ALERT, {
          type: 'error',
          message: `Your deposit was failed!`,
        });
        setDepositUrl('');
      }
    }
  }, []);

  const onAmountChange = useCallback((e) => {
    setAmount(e.target.value);
  }, []);

  const deposit = useCallback(async () => {
    if (depositLoading || !activeOperator || remainingData?.remainingText) {
      return;
    }

    let valid = true;

    if (amount < activeOperator.MinAmount) {
      valid = false;
    }

    if (amount > activeOperator.MaxAmount) {
      valid = false;
    }

    if (!valid) {
      setLocalAlertData({
        type: 'error',
        message: intl.formatMessage(
          {
            id: 'min_amount_max_amount',
          },
          {
            minAmount: activeOperator.MinAmount,
            maxAmount: activeOperator.MaxAmount,
          }
        ),
      });
      return;
    }
    try {
      setDepositLoading(true);
      const response = await deposit_req(activeOperator.ID, amount);

      if (!response?.url) {
        setDepositLoading(false);
        setDepositMessage(response?.msg || `Your deposit was successful!`);

        const nextTimestamp = new Date(Date.now() + 10 * 1000);
        setRemainingData({
          processingTime: nextTimestamp,
          remainingText: '00:10',
        });
      } else if (response?.depositUrlType === 2) {
        window.location.href = response.url;
      } else {
        setDepositUrl(response.url);
      }
    } catch (e) {
      setLocalAlertData({
        type: 'error',
        message: `Something went wrong! Please contact our support.`,
      });
      setRemainingData({
        processingTime: null,
        remainingText: '',
      });
      clearTimeout(processingTimerRef.current);
    } finally {
      setDepositLoading(false);
    }
  }, [amount, activeOperator, depositLoading, remainingData?.remainingText]);

  const calculateRemainingTime = () => {
    if (!remainingData.processingTime) {
      return;
    }
    const remaining = remainingData.processingTime - Date.now();

    if (remaining >= 0) {
      const { minutes, seconds } = remainingMinSecs(remaining);

      processingTimerRef.current = setTimeout(() => {
        setRemainingData({
          processingTime: remainingData?.processingTime,
          remainingText: `${minutes}:${seconds}`,
        });
      }, 1000);
    } else {
      setRemainingData({
        processingTime: null,
        remainingText: '',
      });
      if (depositMessage) {
        setLocalAlertData({
          type: 'success',
          message: depositMessage,
        });
      }
    }
  };

  useEffect(() => {
    calculateRemainingTime();
  }, [remainingData]);

  useEffect(() => {
    if (localAlertData) {
      localAlertTimeoutRef.current = setTimeout(() => {
        setLocalAlertData(null);
        setDepositMessage('');
      }, 7000);
    }
  }, [localAlertData]);

  useEffect(() => {
    window.addEventListener('message', onIframeEvent);

    return () => {
      window.removeEventListener('message', onIframeEvent);
    };
  }, []);

  return (
    <>
      <div className="deposit_with_partner">
        {amounts?.length ? (
          <div className="amounts_wrapper">
            {amounts.map((val) => (
              <NumericFormat
                key={val}
                value={val}
                displayType="text"
                thousandSeparator
                renderText={(value, renderProps) => (
                  <div className="chip_with_present">
                    <Chip
                      label={value}
                      selected={amount === val}
                      onClick={() => {
                        setAmount(val);
                      }}
                    />
                    {amountsWithPresent.current.includes(val) ? (
                      <div className="present_wrapper">
                        <Image
                          src={PresentIcon}
                          alt="Present"
                          width={12}
                          height={12}
                        />
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                )}
              />
            ))}
          </div>
        ) : (
          <></>
        )}
        <div className="inputs_wrapper">
          {remainingData?.remainingText ? (
            <div className="processing_info">Your deposit is processing...</div>
          ) : (
            <TextField
              type="number"
              placeholder={intl.messages.amount}
              value={amount}
              onChange={onAmountChange}
            />
          )}
          <Button
            variant="contained"
            onClick={() => deposit()}
            loading={depositLoading}
            disabled={depositLoading}
          >
            {remainingData?.remainingText || intl.messages.deposit}
          </Button>
        </div>
        {activeOperator &&
          activeOperator?.MinAmount &&
          activeOperator?.MaxAmount && (
            <span className="min_max">
              {intl.formatMessage(
                {
                  id: 'min_amount_max_amount',
                },
                {
                  minAmount: activeOperator?.MinAmount,
                  maxAmount: activeOperator?.MaxAmount,
                }
              )}
            </span>
          )}
        <p className="hint_text" />
        {depositUrl && (
          <div className="iframe_wrapper">
            <div className="iframe_inner">
              <span
                className="icon-close"
                onClick={() => setDepositUrl(false)}
              />
              <iframe
                src={depositUrl}
                title={`${websiteConfig.name} deposit`}
                className="iframe"
                ref={iframeRef}
                allowFullScreen
              />
            </div>
          </div>
        )}
      </div>

      <style jsx>{styles}</style>
    </>
  );
}
